import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import { Icon, Button } from 'Components';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import useAudienceTenantId from 'LearningManager/hooks/useAudienceTenantId';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';

const Organization = observer(() => {
	const { assignUsersStore } = useStores();
	const { assignmentOrganizations: organizations, status } = assignUsersStore;
	const { tenantId, isTenantResolved } = useAudienceTenantId(
		useAssignmentTenantId()
	);

	const {
		draft: {
			customSetup: { selectedOrganizations }
		},
		toggleAssignUsersSelection,
		setAssignUsersSelection
	} = useStepper();

	useEffect(() => {
		if (!isTenantResolved) return;
		assignUsersStore.fetchAssignmentOrganizations(tenantId);
	}, [isTenantResolved, tenantId]);

	const handleCheckboxChange = (id: number | string, checked: boolean) => {
		const numId = Number(id);
		const org = organizations.find((o) => o.id === numId);
		if (!org) return;
		if (checked !== selectedOrganizations.some((o) => o.id === numId)) {
			toggleAssignUsersSelection('selectedOrganizations', {
				id: org.id,
				name: org.name,
				label: org.label
			});
		}
	};

	const handleSelectAll = () => {
		if (selectedOrganizations.length === organizations.length) {
			setAssignUsersSelection('selectedOrganizations', []);
		} else {
			setAssignUsersSelection(
				'selectedOrganizations',
				organizations.map((o) => ({
					id: o.id,
					name: o.name,
					label: o.label
				}))
			);
		}
	};

	const isAllSelected =
		selectedOrganizations.length === organizations.length;
	const isLoading = status.fetchAssignmentOrganizations === Status.Pending;

	return (
		<div id="lms-ca-assign-users-organization">
			<div className={styles.header}>
				<Heading
					titleClassName={styles.title}
					title="Organization"
					astrix={true}
				/>
				{organizations.length > 1 && (
					<Button
						id="lms-ca-au-organization-select-all"
						className={styles.selectAllBtn}
						onClick={handleSelectAll}
						type="link"
						size="middle"
					>
						{isAllSelected ? 'Deselect all' : 'Select all'}
					</Button>
				)}
			</div>
			<div id="lms-ca-au-organization-list" className={styles.container}>
				{isLoading && (
					<div className={styles.loading}><Spin /></div>
				)}
				{organizations.map((org) => {
					const isChecked = selectedOrganizations.some(
						(o) => o.id === org.id
					);
					return (
						<CheckBoxCard
							key={org.id}
							id={org.id}
							htmlId={`lms-ca-au-organization-${org.id}`}
							icon={
								<Icon
									type={isChecked ? 'orgOptFilled' : 'orgOptIcon'}
								/>
							}
							title={org.name}
							desc={org.label}
							isChecked={isChecked}
							onChange={handleCheckboxChange}
						/>
					);
				})}
			</div>
		</div>
	);
});

export default Organization;
